import { useMutation } from "@apollo/client";
import { AddStarMutation } from "components/engagement/star/graphql/AddStarMutation";
import { RemoveStarMutation } from "components/engagement/star/graphql/RemoveStarMutation";
import type {
  IAddStarMutation,
  IAddStarMutationVariables,
} from "components/engagement/star/graphql/__generated__/AddStarMutation.generated";
import type {
  IRemoveStarMutation,
  IRemoveStarMutationVariables,
} from "components/engagement/star/graphql/__generated__/RemoveStarMutation.generated";
import classNames from "components/ui/classNames";
import { StarIcon } from "lucide-react";
import type { IStarrable } from "types/graphqlTypes";

interface StarButtonProps {
  starrable: IStarrable;
  disabled?: boolean;
  onAddStar?: () => void;
  onRemoveStar?: () => void;
}
export default function StarButton({ disabled, starrable, onAddStar, onRemoveStar }: StarButtonProps) {
  const [addStar, { loading: isAddingStar }] = useMutation<IAddStarMutation, IAddStarMutationVariables>(
    AddStarMutation,
    {
      variables: {
        input: {
          starrableId: starrable.objectId,
        },
      },
    },
  );
  const [removeStar, { loading: isRemovingStar }] = useMutation<IRemoveStarMutation, IRemoveStarMutationVariables>(
    RemoveStarMutation,
    {
      variables: {
        input: {
          starrableId: starrable.objectId,
        },
      },
    },
  );
  const valueFormatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  const isMutating = isAddingStar || isRemovingStar;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (isMutating) {
          return;
        }

        if (starrable.viewerHasStarred) {
          removeStar({
            onCompleted: onRemoveStar,
          });
          return;
        }

        addStar({
          onCompleted: onAddStar,
        });
      }}
      className={classNames(
        "group relative inline-flex h-10 w-16 items-center justify-center space-x-2 p-2",
        "rounded-l-xl border border-slate-200 bg-slate-50/60",
        "dark:border-slate-950 dark:bg-slate-700",
        "focus-visible:outline-none",
        "focus-visible:ring-1 focus-visible:ring-offset-1",
        "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
        "focus-visible:border-transparent focus-visible:bg-gray-50",
        "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
        "disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      <StarIcon
        width={16}
        height={16}
        className={classNames("h-4 w-4 fill-slate-50 stroke-slate-500 dark:stroke-slate-50", {
          "fill-orange-50 stroke-orange-600 dark:fill-yellow-400 dark:stroke-yellow-400": starrable.viewerHasStarred,
          "group-hover:fill-yellow-50 group-hover:stroke-yellow-600": !starrable.viewerHasStarred && !disabled,
          "dark:group-hover:fill-yellow-500 dark:group-hover:stroke-yellow-500":
            !starrable.viewerHasStarred && !disabled,
        })}
        strokeWidth={2}
        absoluteStrokeWidth={true}
      />

      <span className={classNames("text-sm leading-5 text-gray-600 dark:font-bold dark:text-slate-300")}>
        {valueFormatter.format(starrable.stargazerCount)}
      </span>
    </button>
  );
}
